/* /assets/os-pdp.js
 *
 * OS CUSTOM (upgrade-safe, new file). Behaviour for the product page v2
 * template (sections/os-pdp-main.liquid).
 *
 * Everything is wired by event delegation on document, or by idempotent
 * init() calls on elements that carry data-os-* hooks. The product section
 * is re-rendered by the theme on every size change (productForm() ->
 * $sectionApi.navigate -> Alpine.morph). The parts this file drives
 * (gallery, film, delivery countdown, drawer, Styled with) sit under
 * data-morph-skip so they are not touched by that re-render; the parts
 * that are not (title, price, size buttons, sticky bar label) are
 * re-rendered by the server and need no JS state.
 *
 * Every add path posts an explicit variant id:
 *   - main button and sticky bar: the theme's own form (hidden id input,
 *     kept in step by productForm() and assets/os-product-form.js)
 *   - size guide drawer and Styled with: POST /cart/add.js with the id of
 *     the size button that is pressed, never "the first available size".
 *
 * Analytics: events are pushed to window.dataLayer (GTM -> GA4), the same
 * route as assets/os-image-toggle.js. Every event carries product_id and
 * product_title. Events: size_guide_open, store_stock_open, alterations_open,
 * styled_with_add (+ added_product_id), sticky_atc_add (+ device), detail_image_open,
 * question_open (+ question), notify_me_open, os_film_play, os_delivery_date_shown
 * {country}, os_styled_with_click.
 */
(function () {
  'use strict';

  if (window.__osPdpLoaded) return;
  window.__osPdpLoaded = true;

  var doc = document;
  var body = doc.body;
  var inited = new WeakSet();

  /* ---------------------------------------------------------------- helpers */

  function qs(sel, root) { return (root || doc).querySelector(sel); }
  function qsa(sel, root) { return Array.prototype.slice.call((root || doc).querySelectorAll(sel)); }

  function pageHandle() {
    var el = qs('[data-section-type="os-pdp-main"]');
    return el ? el.getAttribute('data-product-handle') : '';
  }

  function track(event, data) {
    window.dataLayer = window.dataLayer || [];
    var root = qs('[data-section-type="os-pdp-main"]');
    var payload = {
      event: event,
      product_handle: pageHandle(),
      product_id: root ? root.getAttribute('data-product-id') : '',
      product_title: root ? root.getAttribute('data-product-title') : ''
    };
    for (var k in data) { if (Object.prototype.hasOwnProperty.call(data, k)) payload[k] = data[k]; }
    window.dataLayer.push(payload);
  }

  function cartRoute() {
    return (window.theme && window.theme.routes && window.theme.routes.cart) || '/cart';
  }

  /* Same path as productForm() handleSuccess in theme.js: refresh the header
     total, then open the cart (or reload it if the cart is already open). */
  function openCartAfterAdd(item) {
    return fetch(cartRoute() + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        doc.dispatchEvent(new CustomEvent('theme:cart:change', { detail: { cart: cart }, bubbles: true }));
      })
      .catch(function () {})
      .then(function () {
        if (window.theme && window.theme.state && window.theme.state.cartOpen) {
          doc.dispatchEvent(new CustomEvent('theme:cart:reload', { bubbles: true }));
        } else {
          var popdown = qs('[data-product-add-popdown-wrapper]') || doc;
          popdown.dispatchEvent(new CustomEvent('theme:cart:popdown', { detail: { variant: item }, bubbles: true }));
        }
      });
  }

  function addVariant(variantId) {
    return fetch(cartRoute() + '/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ id: Number(variantId), quantity: 1 })
    }).then(function (r) {
      return r.json().then(function (json) {
        if (!r.ok) throw json;
        return json;
      });
    });
  }

  function errorText(err) {
    if (err && typeof err.description === 'string') return err.description;
    if (err && typeof err.message === 'string') return err.message;
    return 'Could not add to bag. Please try again.';
  }

  /* --------------------------------------------------------- delivery date */

  var WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  var MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  var DAY = 86400000;

  function londonNow(date) {
    var parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/London',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hourCycle: 'h23'
    }).formatToParts(date);
    var o = {};
    parts.forEach(function (p) { if (p.type !== 'literal') o[p.type] = parseInt(p.value, 10); });
    return { civil: Date.UTC(o.year, o.month - 1, o.day), seconds: o.hour * 3600 + o.minute * 60 + o.second };
  }

  function isoOf(civil) {
    var d = new Date(civil);
    function p(n) { return (n < 10 ? '0' : '') + n; }
    return d.getUTCFullYear() + '-' + p(d.getUTCMonth() + 1) + '-' + p(d.getUTCDate());
  }

  function weekdayOf(civil) {
    var n = new Date(civil).getUTCDay();
    return n === 0 ? 7 : n; // 1 = Monday ... 7 = Sunday
  }

  function parseList(str) {
    return String(str || '').split(/[\s,]+/).filter(Boolean);
  }

  /* Rules (settings: OS Product page v2):
       - orders placed before the cut-off (Europe/London) on a dispatch day
         go out that day, otherwise on the next dispatch day
       - delivery is the first delivery day after dispatch
       - bank holidays are skipped for both
     cfg: { cutoff: hour, dispatch: [1..7], delivery: [1..7], holidays: ['YYYY-MM-DD'] } */
  function estimate(now, cfg) {
    var holidays = {};
    cfg.holidays.forEach(function (h) { holidays[h] = true; });
    function canDispatch(c) { return cfg.dispatch.indexOf(weekdayOf(c)) !== -1 && !holidays[isoOf(c)]; }
    function canDeliver(c) { return cfg.delivery.indexOf(weekdayOf(c)) !== -1 && !holidays[isoOf(c)]; }

    var ln = londonNow(now);
    var cutoffSeconds = cfg.cutoff * 3600;
    var dispatch = ln.civil;
    var sameDay = canDispatch(dispatch) && ln.seconds < cutoffSeconds;
    var guard = 0;
    if (!sameDay) {
      dispatch += DAY;
      while (!canDispatch(dispatch) && guard++ < 60) dispatch += DAY;
    }
    var delivery = dispatch + DAY;
    guard = 0;
    while (!canDeliver(delivery) && guard++ < 60) delivery += DAY;

    return {
      sameDay: sameDay,
      secondsLeft: sameDay ? cutoffSeconds - ln.seconds : 0,
      deliveryCivil: delivery,
      deliveryLabel: WEEKDAYS[new Date(delivery).getUTCDay()] + ' ' + new Date(delivery).getUTCDate() + ' ' + MONTHS[new Date(delivery).getUTCMonth()]
    };
  }

  function countdownLabel(seconds) {
    var minutes = Math.max(1, Math.floor(seconds / 60));
    var h = Math.floor(minutes / 60);
    var m = minutes % 60;
    return h > 0 ? h + 'h ' + m + 'm' : m + 'm';
  }

  function renderDelivery(el) {
    var line = qs('[data-os-delivery-line]', el);
    if (!line) return;
    var cfg = {
      cutoff: parseInt(el.getAttribute('data-cutoff'), 10) || 14,
      dispatch: parseList(el.getAttribute('data-dispatch-days')).map(Number),
      delivery: parseList(el.getAttribute('data-delivery-days')).map(Number),
      holidays: parseList(el.getAttribute('data-holidays'))
    };
    var est = estimate(new Date(), cfg);
    line.textContent = est.sameDay
      ? 'Order in ' + countdownLabel(est.secondsLeft) + ' for delivery ' + est.deliveryLabel
      : 'Order now for delivery ' + est.deliveryLabel;
  }

  function initDelivery() {
    qsa('[data-os-delivery]').forEach(function (el) {
      if (inited.has(el)) return;
      inited.add(el);
      var country = el.getAttribute('data-country') || '';
      var isUk = country === 'GB';
      if (isUk) {
        renderDelivery(el);
        setInterval(function () { renderDelivery(el); }, 30000);
      }
      track('os_delivery_date_shown', { country: country, shown: isUk ? 'estimate' : 'checkout' });
    });
  }

  /* ------------------------------------------------------------------ film */

  function attachFilm(video) {
    if (video.getAttribute('data-os-loaded')) return;
    video.setAttribute('data-os-loaded', '1');
    qsa('source[data-src]', video).forEach(function (s) { s.src = s.getAttribute('data-src'); });
    video.load();
  }

  function playFilm(video) {
    attachFilm(video);
    video.muted = true;
    var p = video.play();
    if (p && p.catch) p.catch(function () {});
  }

  var filmTracked = false;
  function trackFilm() {
    if (filmTracked) return;
    filmTracked = true;
    track('os_film_play', {});
  }

  /* No visible controls. The film plays while on screen and pauses off screen. Where the
     device asks for reduced motion (or the browser refuses to autoplay) it stays on its poster. */
  function initFilm() {
    var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    qsa('[data-os-film]').forEach(function (cell) {
      if (inited.has(cell)) return;
      inited.add(cell);
      var video = qs('[data-os-film-video]', cell);
      if (!video || reduced) return;
      video.addEventListener('play', trackFilm);
      if (!('IntersectionObserver' in window)) return;
      new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) playFilm(video);
          else video.pause();
        });
      }, { threshold: 0.35 }).observe(cell);
    });
  }

  /* ---------------------------------------------------- mobile gallery counter */

  function initCounter() {
    var grid = qs('[data-os-grid]');
    var counter = qs('[data-os-counter]');
    if (!grid || !counter || inited.has(grid)) return;
    inited.add(grid);
    var total = qsa('[data-os-cell]', grid).length;
    grid.addEventListener('scroll', function () {
      if (!grid.clientWidth) return;
      var i = Math.round(grid.scrollLeft / grid.clientWidth);
      counter.textContent = Math.min(total, i + 1) + ' / ' + total;
    }, { passive: true });
  }

  /* ------------------------------------------------------------ sticky bar */
  // Shows once the main Add to cart is fully out of view (scrolled past), stays while it is out
  // of view, and hides once it is back in view. A 24px band either side of the edge keeps the
  // state as it is, so it cannot flicker when the button sits at the edge. It never shows while a
  // popup, the size sheet or the image viewer is open, and it hides at the footer.
  var sticky = { on: false, footer: false, bound: false, raf: 0 };

  function overlayOpen() {
    return !!md.el || !!sw.openCard || !!(zoom && zoom.gallery) || !!qs('.pswp--open');
  }

  function stickyUpdate() {
    sticky.raf = 0;
    var button = qs('form[data-product-form] [data-add-to-cart]');
    var bar = qs('[data-os-sticky]');
    if (!button || !bar) return;
    // on mobile the Shop Pay / Apple Pay button sits under Add to basket: the bar waits until that has gone too
    var pay = qs('form[data-product-form] shopify-accelerated-checkout, form[data-product-form] .shopify-payment-button');
    var ref = pay && pay.offsetParent !== null && pay.getBoundingClientRect().height > 0 ? pay : button;
    var r = ref.getBoundingClientRect();
    if (r.bottom < -24) sticky.on = true;
    else if (r.bottom > 24) sticky.on = false;
    var footer = qs('footer, .shopify-section-group-footer-group');
    sticky.footer = !!footer && footer.getBoundingClientRect().top < window.innerHeight;
    var show = sticky.on && !sticky.footer && !overlayOpen();
    doc.documentElement.classList.toggle('os-pdp-sticky-on', show);
  }

  function requestStickyUpdate() {
    if (!sticky.raf) sticky.raf = window.requestAnimationFrame(stickyUpdate);
  }

  function initSticky() {
    if (sticky.bound || !qs('[data-os-sticky]')) return;
    sticky.bound = true;
    window.addEventListener('scroll', requestStickyUpdate, { passive: true });
    window.addEventListener('resize', requestStickyUpdate);
    new MutationObserver(requestStickyUpdate).observe(body, { attributes: true, attributeFilter: ['class'] });
    stickyUpdate();
  }

  function syncSticky() {
    var on = doc.documentElement.classList.contains('os-pdp-sticky-on');
    qsa('[data-os-sticky]').forEach(function (bar) {
      bar.setAttribute('aria-hidden', on ? 'false' : 'true');
      var b = qs('[data-os-sticky-add]', bar);
      if (b) b.tabIndex = on ? 0 : -1;
      qsa('[data-os-sticky-sz]', bar).forEach(function (z) { z.tabIndex = on ? 0 : -1; });
    });
  }

  new MutationObserver(syncSticky).observe(doc.documentElement, { attributes: true, attributeFilter: ['class'] });

  // True when the variant in the form's hidden id input can be bought.
  function selectedVariantAvailable(form) {
    try {
      var data = JSON.parse(qs('[data-product-json]', form).textContent);
      var id = String(qs('input[name="id"]', form).value);
      var v = data.variants.filter(function (x) { return String(x.id) === id; })[0];
      return v ? !!v.available : true;
    } catch (e) {
      return true;
    }
  }

  /* -------------------------------------- the sticky bar's size (never pre-selected) */
  // `chosen` is the variant the SHOPPER has picked (on the page, in the bar or in the size sheet). The page's own
  // default size is not a choice, so until one is made the bar's button reads "Select a size". Desktop: compact
  // size boxes in the bar. Mobile: the button opens the size sheet. A choice made anywhere is the same size
  // everywhere: it is picked on the page through the same radio (so the theme updates the price and stock as for
  // a click on the page) and shown in the bar and the sheet.
  var pick = { chosen: '', programmatic: false };
  var PICK_MQ = window.matchMedia('(max-width: 767px)');

  function barHasSizes() { return !!qs('[data-os-sticky-sizes]'); }
  function sizeAvailable(id) {
    var z = qs('[data-os-sticky-sz][data-variant-id="' + id + '"]') || qs('[data-os-pick-size][data-variant-id="' + id + '"]');
    return !z || z.getAttribute('data-available') !== 'false';
  }

  function renderSticky() {
    qsa('[data-os-sticky-sz], [data-os-pick-size]').forEach(function (z) {
      z.setAttribute('aria-pressed', z.getAttribute('data-variant-id') === pick.chosen ? 'true' : 'false');
    });
    qsa('[data-os-sticky-add]').forEach(function (btn) {
      if (!barHasSizes()) return;
      var label;
      if (!pick.chosen) label = 'Select a size';
      else if (sizeAvailable(pick.chosen)) label = 'Add to basket';
      else label = 'Notify me when available';
      if (btn.textContent.trim() !== label) btn.textContent = label;
      btn.setAttribute('data-state', !pick.chosen ? 'select' : sizeAvailable(pick.chosen) ? 'add' : 'notify');
    });
  }

  function chooseSize(id) {
    pick.chosen = String(id);
    var input = qs('.os-pdp-sizes input[data-variant-id="' + id + '"]');
    var label = input && input.id && qs('label[for="' + input.id + '"]');
    if (label && !input.checked) {
      pick.programmatic = true;
      label.click();
      setTimeout(function () { pick.programmatic = false; }, 50);
    }
    renderSticky();
  }

  // A size chosen on the page is the bar's size too (a click by the shopper, not the page's default).
  doc.addEventListener('change', function (e) {
    var input = e.target.closest && e.target.closest('.os-pdp-sizes input[data-variant-id]');
    if (!input || pick.programmatic) return;
    pick.chosen = input.getAttribute('data-variant-id');
    renderSticky();
  });

  doc.addEventListener('click', function (e) {
    var z = e.target.closest && e.target.closest('[data-os-sticky-sz]');
    if (z) { chooseSize(z.getAttribute('data-variant-id')); return; }
    var sheetPick = e.target.closest && e.target.closest('[data-os-pick-size]');
    if (sheetPick) {
      chooseSize(sheetPick.getAttribute('data-variant-id'));
      closeModal();
      var again = qs('[data-os-sticky-add]');
      if (again) setTimeout(function () { again.focus(); }, 60);
      return;
    }
    var add = e.target.closest('[data-os-sticky-add]');
    if (!add) return;
    if (barHasSizes() && !pick.chosen) {
      // No size yet: mobile opens the size sheet, desktop moves to the size boxes in the bar.
      if (PICK_MQ.matches) { openModal('size-picker', add); return; }
      var first = qs('[data-os-sticky-sz]');
      if (first) first.focus();
      return;
    }
    var form = qs('form[data-product-form]');
    var main = form && qs('[data-add-to-cart]', form);
    var chosenAvailable = barHasSizes() ? sizeAvailable(pick.chosen) : (!!main && !main.disabled && selectedVariantAvailable(form));
    if (!chosenAvailable) {
      // Sold out: the bar's button is Notify me when available, which opens our popup.
      openModal('notify', add);
      return;
    }
    if (!main || main.disabled) return;
    var go = function () {
      track('sticky_atc_add', { device: window.innerWidth >= 990 ? 'desktop' : 'mobile' });
      if (form.requestSubmit) form.requestSubmit(main);
      else main.click();
    };
    if (barHasSizes() && mainVariantId() !== pick.chosen) {
      chooseSize(pick.chosen);
      setTimeout(go, 700); // the page re-renders for the new size first
    } else go();
  });

  // The bar's price follows the page's price (the bar is outside the page's re-render).
  function mirrorStickyPrice() {
    var src = qs('.os-pdp__sticky .product__price .os-price');
    var dst = qs('[data-os-sticky-meta]');
    if (!src || !dst) return;
    var copy = src.cloneNode(true);
    copy.querySelectorAll('[data-product-price], [data-compare-price]').forEach(function (n) { n.removeAttribute('data-product-price'); n.removeAttribute('data-compare-price'); });
    var html = copy.outerHTML.replace(/\s*GBP/g, '');
    if (dst.innerHTML.trim() !== html.trim()) dst.innerHTML = html;
  }
  var priceRaf = 0;
  function watchStickyPrice() {
    var col = qs('[data-os-sticky-col]');
    if (!col || col._osPriceWatch) return;
    col._osPriceWatch = true;
    new MutationObserver(function () { if (!priceRaf) priceRaf = requestAnimationFrame(function () { priceRaf = 0; mirrorStickyPrice(); }); }).observe(col, { childList: true, subtree: true, characterData: true });
  }

  function initStickyPick() {
    if (/[?&]variant=/.test(location.search)) pick.chosen = mainVariantId();
    renderSticky();
    watchStickyPrice();
  }

  // Notify me when available: our own popup. It makes the same call the live product form's
  // back in stock does (assets/os-bis.js): Klaviyo's client API, the company id from
  // window.osBisSettings (set in os-body.liquid from the theme setting), revision 2026-07-15,
  // 202 means subscribed. Same success and error wording as there.
  function osBisCompanyId() { return (window.osBisSettings && window.osBisSettings.companyId) || ''; }

  var NEWS_LIST_FALLBACK = 'VKeRMX';
  var KLAVIYO_REVISION = '2026-07-15';
  function klaviyoPost(url, body) {
    return fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/vnd.api+json', revision: KLAVIYO_REVISION }, body: JSON.stringify(body) });
  }
  function esc(s) { var d = doc.createElement('div'); d.textContent = s; return d.innerHTML; }

  doc.addEventListener('submit', function (e) {
    var form = e.target.closest && e.target.closest('[data-os-notify]');
    if (!form) return;
    e.preventDefault();
    var email = (qs('input[name="email"]', form).value || '').trim();
    var msg = qs('[data-os-notify-msg]', form);
    var btn = qs('button[type="submit"]', form);
    var wantsNews = !!(qs('input[name="newsletter"]', form) || {}).checked;
    msg.textContent = '';
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { msg.textContent = 'Please enter a valid email address.'; return; }
    var company = osBisCompanyId() || form.getAttribute('data-company') || '';
    var variant = form.getAttribute('data-variant') || mainVariantId();
    var failure = 'Something went wrong there. Please try again in a moment.';
    if (!company || !variant) { msg.textContent = failure; return; }
    btn.disabled = true;
    var bis = klaviyoPost('https://a.klaviyo.com/client/back-in-stock-subscriptions?company_id=' + company, {
      data: {
        type: 'back-in-stock-subscription',
        attributes: { profile: { data: { type: 'profile', attributes: { email: email } } }, channels: ['EMAIL'] },
        relationships: { variant: { data: { type: 'catalog-variant', id: '$shopify:::$default:::' + variant } } }
      }
    }).then(function (r) { return r.status === 202; }).catch(function () { return false; });
    // Newsletter: only when the box was ticked. Same client call as the homepage sign-up, with consent.
    var news = wantsNews
      ? klaviyoPost('https://a.klaviyo.com/client/subscriptions/?company_id=' + company, {
          data: {
            type: 'subscription',
            attributes: {
              custom_source: 'Product page notify me',
              profile: { data: { type: 'profile', attributes: { email: email, subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } } } } }
            },
            relationships: { list: { data: { type: 'list', id: form.getAttribute('data-list') || NEWS_LIST_FALLBACK } } }
          }
        }).then(function (r) { return r.status === 202; }).catch(function () { return false; })
      : Promise.resolve(false);
    Promise.all([bis, news]).then(function (res) {
      btn.disabled = false;
      if (!res[0]) { msg.textContent = failure; return; }
      var size = form.getAttribute('data-size');
      var done = qs('[data-os-notify-done]', form.parentNode);
      done.innerHTML =
        '<h3 class="os-pdp-notify__done-h">You\u2019re on the list.</h3>' +
        '<p class="os-pdp-notify__done-p">We\u2019ll email you once if ' + (size ? 'size ' + esc(size) : 'this item') + ' comes back in stock.</p>' +
        (wantsNews && res[1] ? '<p class="os-pdp-notify__done-p">And you\u2019re signed up to our newsletter.</p>' : '') +
        '<button type="button" class="os-pdp-sg__add os-pdp-notify__close" data-os-modal-close>Close</button>';
      form.hidden = true;
      form.reset();
      var close = qs('.os-pdp-notify__close', done);
      if (close) close.focus();
    });
  });

  /* ------------------------------------------------------- full-screen zoom */

  var zoom = { gallery: null, pushed: false, loading: null };

  function loadPhotoSwipe() {
    if (window.themePhotoswipe) return Promise.resolve();
    if (zoom.loading) return zoom.loading;
    zoom.loading = new Promise(function (resolve, reject) {
      var s = doc.createElement('script');
      s.src = window.theme && window.theme.assets && window.theme.assets.photoswipe;
      s.onload = resolve;
      s.onerror = reject;
      doc.head.appendChild(s);
    });
    return zoom.loading;
  }

  function openZoom(trigger) {
    var group = trigger.closest('[data-os-gallery], [data-os-details]');
    var triggers = qsa('[data-os-zoom]', group);
    var index = triggers.indexOf(trigger);
    if (index < 0) return;
    if (group.hasAttribute('data-os-details')) track('detail_image_open', { detail_index: index + 1, detail_caption: trigger.getAttribute('data-caption') || '' });
    var items = triggers.map(function (t) {
      var img = qs('img', t);
      return { src: t.getAttribute('data-src'), w: parseInt(t.getAttribute('data-w'), 10), h: parseInt(t.getAttribute('data-h'), 10), msrc: img && (img.currentSrc || img.src) };
    });
    loadPhotoSwipe().then(function () {
      var PS = window.themePhotoswipe.PhotoSwipe.default;
      var UI = window.themePhotoswipe.PhotoSwipeUI.default;
      var el = qs('[data-zoom-wrapper]');
      if (!el || zoom.gallery) return;
      var gallery = new PS(el, UI, items, {
        index: index,
        showHideOpacity: true,
        showAnimationDuration: 150,
        hideAnimationDuration: 250,
        bgOpacity: 1,
        spacing: 0,
        maxSpreadZoom: 4,
        history: false,
        loop: false,
        pinchToClose: false,
        closeOnScroll: false,
        closeOnVerticalDrag: true,
        getThumbBoundsFn: function (i) {
          var t = triggers[i] || trigger;
          var rect = t.getBoundingClientRect();
          return { x: rect.left, y: rect.top + (window.pageYOffset || doc.documentElement.scrollTop), w: rect.width };
        }
      });
      zoom.gallery = gallery;
      el.dispatchEvent(new CustomEvent('theme:scroll:lock', { bubbles: true }));
      gallery.listen('close', function () {
        doc.dispatchEvent(new CustomEvent('theme:scroll:unlock', { bubbles: true }));
        if (zoom.pushed) {
          zoom.pushed = false;
          history.back();
        }
        zoom.gallery = null;
      });
      gallery.init();
      // Scroll to zoom on desktop (PhotoSwipe already zooms on click or tap and on pinch).
      var wheel = function (ev) {
        if (!zoom.gallery) return;
        ev.preventDefault();
        var g = zoom.gallery;
        var min = g.currItem && g.currItem.initialZoomLevel ? g.currItem.initialZoomLevel : 1;
        var next = g.getZoomLevel() * (ev.deltaY < 0 ? 1.18 : 1 / 1.18);
        next = Math.max(min, Math.min(next, 4));
        g.zoomTo(next, { x: ev.clientX, y: ev.clientY }, 120);
      };
      el.addEventListener('wheel', wheel, { passive: false });
      gallery.listen('close', function () { el.removeEventListener('wheel', wheel); });
      // Back closes the viewer rather than leaving the page.
      pushOverlayState({ osPdpZoom: true });
      zoom.pushed = true;
    }).catch(function () {});
  }

  doc.addEventListener('click', function (e) {
    var t = e.target.closest('[data-os-zoom]');
    if (t) openZoom(t);
  });

  /* The theme's router reloads the page when a history entry it marked
     (state.fromRouter, set on every size change) is landed on again. Our
     drawer and zoom push an entry and go back() to close, which would land
     on that marked entry. Clear the mark on the current entry first. */
  function pushOverlayState(state) {
    var s = history.state;
    if (s && s.fromRouter) {
      var copy = {};
      for (var k in s) { if (Object.prototype.hasOwnProperty.call(s, k)) copy[k] = s[k]; }
      copy.fromRouter = false;
      history.replaceState(copy, '', location.href);
    }
    history.pushState(state, '');
  }

  /* ------------------------------------------------------------ popups */
  // One system for the size guide, "Check stock in our shops" and the alterations popup:
  // [data-os-modal="name"] is the popup, [data-os-modal-open="name"] opens it, and
  // [data-os-modal-close] closes it. Escape and the browser Back button close it, focus stays
  // inside while it is open.

  var MODAL_EVENTS = { 'size-guide': 'size_guide_open', stock: 'store_stock_open', alterations: 'alterations_open', notify: 'notify_me_open' };
  var md = { el: null, name: '', pushed: false, opener: null, timer: 0 };

  function mainVariantId() {
    var input = qs('form[data-product-form] input[name="id"]');
    return input ? input.value : '';
  }

  /* Keep the buy column's size in step with the popup (same variant id either way). */
  function syncMain(btn) {
    var id = btn && btn.getAttribute('data-variant-id');
    if (!id) return;
    var input = qs('.os-pdp-sizes input[data-variant-id="' + id + '"]');
    if (!input || input.checked) return;
    var label = input.id && qs('label[for="' + input.id + '"]');
    if (label) label.click();
  }

  function sgSelect(btn, fromUser) {
    var root = md.el || qs('[data-os-modal="size-guide"]');
    qsa('[data-os-sg-size]', root).forEach(function (b) {
      b.setAttribute('aria-pressed', b === btn ? 'true' : 'false');
    });
    var add = qs('[data-os-sg-add]', root);
    var msg = qs('[data-os-sg-msg]', root);
    if (msg) msg.textContent = '';
    if (add && btn) {
      var avail = btn.getAttribute('data-available') !== 'false';
      add.disabled = !avail;
      add.textContent = avail ? add.getAttribute('data-label') : add.getAttribute('data-label-sold-out');
    }
    if (fromUser) syncMain(btn);
  }

  // Show the shop list for a size (default: the size selected on the page) and mark it in the
  // popup's own size switcher.
  var stockCache = {};
  function syncStock(variantId) {
    var root = qs('[data-os-modal="stock"]');
    if (!root) return;
    var current = String(variantId || mainVariantId());
    qsa('[data-os-stock-variant]', root).forEach(function (b) {
      b.hidden = b.getAttribute('data-os-stock-variant') !== current;
    });
    qsa('[data-os-stock-size]', root).forEach(function (b) {
      b.setAttribute('aria-pressed', b.getAttribute('data-variant-id') === current ? 'true' : 'false');
    });
    var block = qs('[data-os-stock-variant="' + current + '"]', root);
    var loading = qs('[data-os-stock-loading]', root);
    if (loading) loading.hidden = true;
    if (!block || block.getAttribute('data-loaded') === 'true') return;
    // Shopify only gives shop stock for the variant in the page request: fetch this size's list.
    if (loading) loading.hidden = false;
    var url = (root.getAttribute('data-product-url') || location.pathname) + '?variant=' + encodeURIComponent(current) + '&sections=os-pdp-stock-shops';
    var pv = /[?&]preview_theme_id=(\d+)/.exec(location.search);
    if (pv) url += '&preview_theme_id=' + pv[1];
    var done = function (html) {
      block.innerHTML = html;
      block.setAttribute('data-loaded', 'true');
      if (loading && String(variantId || mainVariantId()) === current) loading.hidden = true;
    };
    if (stockCache[current]) { done(stockCache[current]); return; }
    fetch(url, { credentials: 'same-origin' })
      .then(function (r) { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then(function (j) {
        var html = j['os-pdp-stock-shops'];
        if (!html) throw new Error('empty');
        stockCache[current] = html;
        done(html);
      })
      .catch(function () {
        var tpl = qs('[data-os-stock-fallback]', root);
        done(tpl ? tpl.innerHTML : '');
        block.removeAttribute('data-loaded');
      });
  }

  // Tabs: Garment measurements / Size conversions
  function sgTab(name) {
    var root = md.el;
    if (!root) return;
    qsa('[data-os-sg-tab]', root).forEach(function (b) {
      var on = b.getAttribute('data-os-sg-tab') === name;
      b.classList.toggle('is-active', on);
      b.setAttribute('aria-selected', on ? 'true' : 'false');
      b.tabIndex = on ? 0 : -1;
    });
    qsa('[data-os-sg-pane]', root).forEach(function (p) { p.hidden = p.getAttribute('data-os-sg-pane') !== name; });
    var lay = qs('.os-pdp-sg__layout', root);
    if (lay) lay.classList.toggle('is-conv', name === 'conv');
    var body = qs('[data-os-sg-body]', root);
    if (body) body.scrollTop = 0;
    sgMore();
  }

  // "More below": a soft fade and a label while there is more to scroll to.
  function sgMore() {
    var root = md.el;
    if (!root) return;
    var body = qs('[data-os-sg-body]', root);
    var wrap = body && body.parentElement;
    if (!wrap) return;
    wrap.classList.toggle('has-more', body.scrollHeight - body.scrollTop - body.clientHeight > 8);
  }
  doc.addEventListener('scroll', function (e) {
    if (e.target && e.target.hasAttribute && e.target.hasAttribute('data-os-sg-body')) sgMore();
  }, true);
  window.addEventListener('resize', sgMore);

  function openModal(name, opener) {
    var root = qs('[data-os-modal="' + name + '"]');
    if (!root || md.el) return;
    if (name === 'size-guide') {
      md.el = root;
      var match = qs('[data-os-sg-size][data-variant-id="' + mainVariantId() + '"]', root);
      if (match) sgSelect(match);
      setTimeout(sgFades, 250);
      setTimeout(sgFades, 700);
    }
    if (name === 'stock') syncStock();
    if (name === 'notify') {
      var nf = qs('[data-os-notify]', root);
      var cur = qs('.os-pdp-sizes input:checked');
      var what = qs('[data-os-notify-what]', root);
      if (what) what.textContent = cur && cur.value ? 'size ' + cur.value : 'this size';
      if (nf) {
        nf.setAttribute('data-variant', mainVariantId());
        nf.setAttribute('data-size', cur && cur.value ? cur.value : '');
        qs('[data-os-notify-msg]', nf).textContent = '';
        nf.hidden = false;
        var dn = qs('[data-os-notify-done]', root);
        if (dn) dn.innerHTML = '';
      }
    }

    clearTimeout(md.timer);
    md.el = root;
    md.name = name;
    md.opener = opener || doc.activeElement;
    root.hidden = false;
    void root.offsetWidth;
    root.classList.add('is-open');
    body.classList.add('os-pdp-sg-open');
    // The scroll lock takes the scrollable element as its detail, so touch scrolling still
    // works inside it on iOS and Android while the page behind stays locked.
    var scroller = qs('.os-pdp-sg__body', root);
    root.dispatchEvent(new CustomEvent('theme:scroll:lock', { bubbles: true, detail: scroller }));
    var panel = qs('[data-os-modal-panel]', root);
    if (panel) panel.focus();
    pushOverlayState({ osPdpModal: name });
    md.pushed = true;
    if (name === 'size-guide') sgTab('measure');
    setTimeout(sgMore, 60);
    if (MODAL_EVENTS[name]) track(MODAL_EVENTS[name], {});
  }

  function closeModal(fromPop) {
    var root = md.el;
    if (!root) return;
    md.el = null;
    root.classList.remove('is-open');
    body.classList.remove('os-pdp-sg-open');
    root.dispatchEvent(new CustomEvent('theme:scroll:unlock', { bubbles: true }));
    md.timer = setTimeout(function () { if (!md.el) root.hidden = true; }, 420);
    if (md.pushed) {
      md.pushed = false;
      if (!fromPop) history.back();
    }
    if (md.opener && md.opener.focus) md.opener.focus();
  }

  function modalFocusables() {
    var panel = qs('[data-os-modal-panel]', md.el);
    return qsa('a[href], button:not([disabled]), summary, input:not([type="hidden"]):not([disabled]), [tabindex]:not([tabindex="-1"])', panel)
      .filter(function (el) { return el.offsetParent !== null; });
  }

  doc.addEventListener('click', function (e) {
    var open = e.target.closest('[data-os-modal-open]');
    if (open) { openModal(open.getAttribute('data-os-modal-open'), open); return; }

    if (!md.el) return;
    if (e.target.closest('[data-os-modal-close]')) { closeModal(false); return; }

    var tab = e.target.closest('[data-os-sg-tab]');
    if (tab) { sgTab(tab.getAttribute('data-os-sg-tab')); return; }

    var unitBtn = e.target.closest('[data-os-sg-units] button');
    if (unitBtn) {
      var unit = unitBtn.getAttribute('data-unit');
      qsa('[data-os-sg-units] button', md.el).forEach(function (b) {
        var on = b === unitBtn;
        b.classList.toggle('selected', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      qsa('.os-pdp-sg__table[data-unit]', md.el).forEach(function (t) { t.hidden = t.getAttribute('data-unit') !== unit; });
      var lab = qs('[data-os-sg-unit-label]', md.el);
      if (lab) lab.textContent = unit === 'in' ? 'in inches' : 'in cm';
      return;
    }

    var size = e.target.closest('[data-os-sg-size]');
    if (size) { sgSelect(size, true); return; }

    var add = e.target.closest('[data-os-sg-add]');
    if (add) {
      var root = md.el;
      var msg = qs('[data-os-sg-msg]', root);
      var pressed = qs('[data-os-sg-size][aria-pressed="true"]', root);
      var single = qs('[data-os-sg-single]', root);
      var id = pressed ? pressed.getAttribute('data-variant-id') : (single ? single.value : '');
      if (!id) { if (msg) msg.textContent = 'Select a size'; return; }
      add.disabled = true;
      addVariant(id).then(function (item) {
        add.disabled = false;
        closeModal(false);
        openCartAfterAdd(item);
      }).catch(function (err) {
        add.disabled = false;
        if (msg) msg.textContent = errorText(err);
      });
    }
  });

  doc.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && sw.openCard) { closeSheet(); return; }
    if (!md.el) return;
    if (e.key === 'Escape') { e.preventDefault(); closeModal(false); return; }
    if (e.key !== 'Tab') return;
    var f = modalFocusables();
    if (!f.length) return;
    var first = f[0];
    var last = f[f.length - 1];
    var active = doc.activeElement;
    var panel = qs('[data-os-modal-panel]', md.el);
    if (e.shiftKey && (active === first || active === panel)) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
  });

  window.addEventListener('popstate', function () {
    if (md.el && md.pushed) closeModal(true);
    if (zoom.gallery && zoom.pushed) {
      zoom.pushed = false;
      zoom.gallery.close();
    }
  });

  doc.addEventListener('click', function (e) {
    var sz = e.target.closest('[data-os-stock-size]');
    if (sz) syncStock(sz.getAttribute('data-variant-id'));
  });

  // The stock popup follows the size chosen on the page.
  doc.addEventListener('change', function (e) {
    if (e.target.closest && e.target.closest('.os-pdp-sizes')) setTimeout(syncStock, 60);
  });

  /* ------------------------------------------------------------- Styled with */
  // Styled with uses the collection card (its own quick add). An add that follows a click on one of
  // these cards is reported as styled_with_add.
  var swClick = { t: 0, id: '', title: '' };
  doc.addEventListener('click', function (e) {
    var card = e.target.closest('.os-pdp-sw product-grid-item');
    if (card && e.target.closest('button')) {
      swClick = { t: Date.now(), id: card.getAttribute('data-item-id') || '', title: (qs('.product__grid__title', card) || {}).textContent || '' };
    }
  }, true);
  doc.addEventListener('theme:cart:change', function () {
    if (swClick.t && Date.now() - swClick.t < 10000) {
      track('styled_with_add', { added_product_id: swClick.id, added_product_title: String(swClick.title).replace(/\s+/g, ' ').trim() });
      swClick.t = 0;
    }
  });

  // Click on a card goes to the product (tracked). Desktop: hover shows the sizes over the
  // image, a click on a size adds that piece to the bag. Mobile: "+" opens the same sizes in a
  // sheet. Every add posts the id of the size that was clicked.
  var sw = { openCard: null, opener: null };

  function openSheet(card, opener) {
    closeSheet();
    sw.opener = opener || null;
    sw.openCard = card;
    card.classList.add('is-open');
    doc.documentElement.classList.add('os-pdp-sw-sheet-open');
    var first = qs('[data-os-sw-size]:not([disabled])', card);
    if (first) first.focus();
  }

  function closeSheet() {
    if (!sw.openCard) return;
    sw.openCard.classList.remove('is-open');
    sw.openCard = null;
    doc.documentElement.classList.remove('os-pdp-sw-sheet-open');
    if (sw.opener && sw.opener.focus) sw.opener.focus();
    sw.opener = null;
  }

  doc.addEventListener('click', function (e) {
    var a = e.target.closest('.os-pdp-sw__list a');
    if (a) track('os_styled_with_click', { styled_product_href: a.getAttribute('href') });

    var plus = e.target.closest('[data-os-sw-plus]');
    if (plus) { openSheet(plus.closest('[data-os-sw-card]'), plus); return; }
    if (e.target.closest('[data-os-sw-close]')) { closeSheet(); return; }

    var size = e.target.closest('[data-os-sw-size]');
    if (size && !size.disabled) {
      var card = size.closest('[data-os-sw-card]');
      size.disabled = true;
      addVariant(size.getAttribute('data-variant-id')).then(function (item) {
        size.disabled = false;
        track('styled_with_add', { added_product_id: card.getAttribute('data-product-id'), added_product_title: card.getAttribute('data-product-title') });
        closeSheet();
        openCartAfterAdd(item);
      }).catch(function () { size.disabled = false; });
      return;
    }
    // Tap outside an open sheet closes it.
    if (sw.openCard && !e.target.closest('.os-pdp-sw__sizes')) closeSheet();
  });

  /* ----------------------------------------------- sticky buy column offset */
  // The buy column is position: sticky and never scrolls inside itself. If it is taller than
  // the screen, a plain sticky top would hide its lower half until the images end, so the
  // offset is set to (screen height - column height): it moves with the page until its bottom
  // meets the bottom of the screen, then sticks there.
  // Whichever column is shorter is the one that sticks: if the buy column is shorter it sticks
  // (as before); if the gallery is shorter (a product with few images) the gallery sticks while the
  // buy column scrolls past. Either way both end level, with no blank block on either side.
  var stickyColBound = false;
  function updateStickyCol() {
    var col = qs('[data-os-sticky-col]');
    var layout = qs('.os-pdp__layout');
    var media = qs('.os-pdp__media');
    if (!col || !layout || !media) return;
    if (window.innerWidth < 990) {
      col.style.removeProperty('--os-pdp-sticky-top');
      media.style.removeProperty('--os-pdp-sticky-top');
      layout.classList.remove('is-media-sticky');
      return;
    }
    var headerH = parseFloat(getComputedStyle(doc.documentElement).getPropertyValue('--header-height')) || 73;
    var topDefault = headerH + 16;
    var colH = col.offsetHeight;
    var mediaH = media.offsetHeight;
    var mediaSticky = mediaH < colH - 8;
    layout.classList.toggle('is-media-sticky', mediaSticky);
    var h = mediaSticky ? mediaH : colH;
    var top = (h + topDefault > window.innerHeight - 16) ? (window.innerHeight - h - 16) : topDefault;
    var px = Math.round(top) + 'px';
    col.style.setProperty('--os-pdp-sticky-top', px);
    media.style.setProperty('--os-pdp-sticky-top', px);
  }
  function initStickyCol() {
    var col = qs('[data-os-sticky-col]');
    if (!col) return;
    updateStickyCol();
    if (stickyColBound) return;
    stickyColBound = true;
    window.addEventListener('resize', updateStickyCol);
    window.addEventListener('load', updateStickyCol);
    if ('ResizeObserver' in window) {
      var ro = new ResizeObserver(updateStickyCol);
      ro.observe(col);
      var m = qs('.os-pdp__media');
      if (m) ro.observe(m);
    }
    doc.addEventListener('toggle', updateStickyCol, true);
  }

  /* ---------------------------------------------------- Recently viewed */
  // The shopper's own history (written by assets/os-home.js), shown on the same product card as
  // the collection pages: the card's HTML comes from /products/{handle}?view=card. This product
  // and its colourways are left out. Nothing to show, nothing shown: no fallback.
  function initRecent() {
    var section = qs('[data-os-recent]');
    if (!section || inited.has(section)) return;
    inited.add(section);
    var grid = qs('[data-os-recent-grid]', section);
    var skip = (section.getAttribute('data-os-exclude') || '').split(',').filter(Boolean);
    var limit = parseInt(section.getAttribute('data-limit'), 10) || 4;
    var handles = [];
    try {
      handles = JSON.parse(window.localStorage.getItem('os_recently_viewed') || '[]');
    } catch (e) { handles = []; }
    handles = handles.filter(function (h) { return typeof h === 'string' && skip.indexOf(h) === -1; }).slice(0, limit);
    if (!handles.length || !grid) return;
    Promise.all(handles.map(function (h) {
      return fetch('/products/' + encodeURIComponent(h) + '?view=card-pdp', { headers: { Accept: 'text/html' } })
        .then(function (r) { return r.ok ? r.text() : ''; })
        .catch(function () { return ''; });
    })).then(function (cards) {
      var html = cards.filter(function (c) { return c && c.trim(); });
      if (!html.length) return;
      html.forEach(function (c) {
        var wrap = doc.createElement('div');
        wrap.innerHTML = c;
        grid.appendChild(wrap);
      });
      section.hidden = false;
    });
  }

  /* ------------------------------------------- the cloth photo and the story photo */
  // Both are slow and gentle, run only while on screen, and stand still under reduced motion.
  function initMotion() {
    var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced || !('IntersectionObserver' in window)) return;
    qsa('[data-os-drift]').forEach(function (frame) {
      if (inited.has(frame)) return;
      inited.add(frame);
      new IntersectionObserver(function (entries) {
        frame.classList.toggle('is-playing', entries[0].isIntersecting);
      }, { threshold: 0.1 }).observe(frame);
    });
    qsa('[data-os-fade]').forEach(function (box) {
      if (inited.has(box)) return;
      var slides = qsa('[data-os-fade-slide]', box);
      if (slides.length < 2) return;
      inited.add(box);
      var i = 0;
      var timer = 0;
      var finished = false;
      // One cycle through the images, then it rests on the last one (WCAG 2.2.2: moving content
      // that starts by itself must stop or be pausable).
      function step() {
        slides[i].classList.remove('is-on');
        i += 1;
        slides[i].classList.add('is-on');
        if (i >= slides.length - 1) { finished = true; clearInterval(timer); }
      }
      new IntersectionObserver(function (entries) {
        clearInterval(timer);
        if (entries[0].isIntersecting && !finished) timer = setInterval(step, 5000);
      }, { threshold: 0.2 }).observe(box);
    });
  }

  /* ---------------------------------------------------------- questions */
  doc.addEventListener('toggle', function (e) {
    var d = e.target;
    if (d && d.matches && d.matches('[data-os-questions] details') && d.open) {
      var s = qs('summary', d);
      track('question_open', { question: s ? s.textContent.trim() : '' });
    }
  }, true);

  /* -------------------------------------------------- gentle fade-in on scroll */
  function initReveal() {
    if (!('IntersectionObserver' in window)) return;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    var io;
    try { io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); }
      });
    }, { threshold: 0.08 }); } catch (err) { return; }
    qsa('.os-pdp-tile, .os-pdp__cell').forEach(function (el) {
      if (inited.has(el)) return;
      var r = el.getBoundingClientRect();
      if (r.top < window.innerHeight || r.left > window.innerWidth) return; // already on screen, or off to the side in the swipe gallery
      inited.add(el);
      doc.documentElement.classList.add('os-pdp-js');
      el.classList.add('os-pdp-reveal');
      try { io.observe(el); } catch (err) { el.classList.add('is-in'); }
    });
    // Safety net: content is never left at opacity 0. If the observer has not revealed an image
    // after 6 seconds (or the observer failed), show everything that is left.
    setTimeout(function () {
      qsa('.os-pdp-reveal:not(.is-in)').forEach(function (el) { el.classList.add('is-in'); });
    }, 6000);
  }

  /* ---------------------------------------------------- Shop the look row */
  // Previous / next arrows for the one-row look cards; shown only when the row overflows.
  function initLookRow() {
    qsa('[data-os-look-row]').forEach(function (wrap) {
      if (inited.has(wrap)) return;
      inited.add(wrap);
      var row = qs('[data-os-look-scroll]', wrap);
      var prev = qs('[data-os-look-prev]', wrap);
      var next = qs('[data-os-look-next]', wrap);
      function update() {
        var over = row.scrollWidth > row.clientWidth + 2;
        if (prev) prev.hidden = !over || row.scrollLeft < 4;
        if (next) next.hidden = !over || row.scrollLeft + row.clientWidth >= row.scrollWidth - 4;
      }
      function by(dir) { row.scrollBy({ left: dir * (row.clientWidth * 0.8), behavior: 'smooth' }); }
      if (prev) prev.addEventListener('click', function () { by(-1); });
      if (next) next.addEventListener('click', function () { by(1); });
      row.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      update();
      setTimeout(update, 800);
      // the cloth's card row runs from the text's left edge to the right edge of the screen
      if (wrap.classList.contains('os-pdp-cloth__rowwrap')) {
        var fit = function () { wrap.style.setProperty('--os-rowwrap-w', Math.max(0, doc.documentElement.clientWidth - wrap.getBoundingClientRect().left) + 'px'); update(); };
        window.addEventListener('resize', fit);
        fit();
        setTimeout(fit, 800);
      }
    });
  }


  /* ------------------------------------------ colourway hover preview (desktop) */
  // Resting the mouse on a colourway thumbnail for 150ms (or focusing it) shows an enlarged 4:5 preview of that
  // colourway's first packshot with its name and price, above the thumbnail row; when there is no room above
  // (the row sits close to the header) it goes to the left of the buy column instead, over the gallery. It never
  // covers the size boxes or Add to basket. aria-hidden: the thumbnail's own label names the colourway. No
  // preview on touch. The colourway's page is prefetched on hover. Fades in fast; no animation under reduced motion.
  function initColourwayPreview() {
    var items = qsa('[data-os-cw-pv]');
    if (!items.length || inited.has(items[0])) return;
    inited.add(items[0]);
    var fine = window.matchMedia('(hover: hover) and (pointer: fine) and (min-width: 990px)');
    if (!fine.matches) return;
    var W = 240, H = 300, GAP = 8, EDGE = 16;
    var pv = doc.createElement('div');
    pv.className = 'os-pdp-cwpv';
    pv.setAttribute('aria-hidden', 'true');
    pv.innerHTML = '<div class="os-pdp-cwpv__img"><img alt="" width="' + W + '" height="' + H + '"></div><p class="os-pdp-cwpv__name"></p><p class="os-pdp-cwpv__price"></p>';
    doc.body.appendChild(pv);
    var img = qs('img', pv), nameEl = qs('.os-pdp-cwpv__name', pv), priceEl = qs('.os-pdp-cwpv__price', pv);
    var timer = 0, current = null, prefetched = {};
    // preload the previews once the page is idle, so a hover shows the picture at once
    (window.requestIdleCallback || function (f) { return setTimeout(f, 1500); })(function () {
      items.forEach(function (li) { var s = li.getAttribute('data-pv-src'); if (s) { var i = new Image(); i.src = s; } });
    });
    function prefetch(url) {
      if (!url || prefetched[url]) return;
      prefetched[url] = true;
      var l = doc.createElement('link'); l.rel = 'prefetch'; l.href = url; doc.head.appendChild(l);
    }
    function place(li) {
      var tile = qs('.os-pdp-cw__tile', li) || li;
      var t = tile.getBoundingClientRect();
      var row = (qs('.os-pdp-cw__list', li.parentNode.parentNode) || li.parentNode).getBoundingClientRect();
      var vw = doc.documentElement.clientWidth, vh = window.innerHeight;
      var ph = pv.offsetHeight || (H + 64);
      var headerH = parseFloat(getComputedStyle(doc.documentElement).getPropertyValue('--header-height')) || 73;
      var left, top;
      if (row.top - GAP - ph >= headerH + 4) {
        // above the row, left edge on the thumbnail; flip to its right edge near the right of the screen
        left = t.left;
        if (left + W > vw - EDGE) left = t.right - W;
        left = Math.max(EDGE, Math.min(left, vw - EDGE - W));
        top = row.top - GAP - ph;
      } else {
        // no room above: to the left of the buy column, its bottom level with the row's bottom
        var col = qs('[data-os-sticky-col]');
        var cl = col ? col.getBoundingClientRect().left : row.left;
        left = Math.max(EDGE, cl - GAP * 2 - W);
        top = Math.min(row.bottom - ph, vh - EDGE - ph);
        top = Math.max(headerH + GAP, top);
      }
      pv.style.left = Math.round(left) + 'px';
      pv.style.top = Math.round(top) + 'px';
    }
    function show(li) {
      current = li;
      img.src = li.getAttribute('data-pv-src') || '';
      nameEl.textContent = li.getAttribute('data-pv-label') || '';
      var was = li.getAttribute('data-pv-was');
      priceEl.innerHTML = '';
      var now = doc.createElement('span');
      now.textContent = li.getAttribute('data-pv-price') || '';
      priceEl.appendChild(now);
      if (was) {
        now.className = 'is-sale';
        var s = doc.createElement('s'); s.textContent = was; priceEl.appendChild(s);
      }
      pv.classList.add('is-on');
      place(li);
    }
    function hide() {
      clearTimeout(timer);
      current = null;
      pv.classList.remove('is-on');
    }
    items.forEach(function (li) {
      li.addEventListener('pointerenter', function (e) {
        if (e.pointerType && e.pointerType !== 'mouse') return;
        prefetch(li.getAttribute('data-pv-url'));
        clearTimeout(timer);
        timer = setTimeout(function () { show(li); }, 150);
      });
      li.addEventListener('pointerleave', hide);
      li.addEventListener('focusin', function () { prefetch(li.getAttribute('data-pv-url')); show(li); });
      li.addEventListener('focusout', hide);
    });
    doc.addEventListener('keydown', function (e) { if (e.key === 'Escape' && current) hide(); });
    window.addEventListener('scroll', hide, { passive: true });
    window.addEventListener('resize', hide);
  }

  /* ---------------------------------------------------- size guide tables: fade on the right when more */
  function sgFades() {
    qsa('.os-pdp-sg__tables').forEach(function (box) {
      var wrap = qs('.os-pdp-sg__table-wrap', box);
      var table = qs('table', box);
      if (!wrap || !table) return;
      if (qsa('.os-pdp-sg__table-wrap', box).length > 1) wrap = qsa('.os-pdp-sg__table-wrap', box).filter(function (w) { var tb = qs('table', w); return tb && !tb.hidden; })[0] || wrap;
      var more = wrap.scrollWidth - wrap.clientWidth - wrap.scrollLeft > 2;
      box.classList.toggle('has-more', more);
      if (!wrap._sgBound) {
        wrap._sgBound = true;
        wrap.addEventListener('scroll', sgFades, { passive: true });
      }
    });
  }
  window.addEventListener('resize', sgFades);
  doc.addEventListener('click', function (e) { if (e.target.closest && e.target.closest('[data-os-sg-units] button')) setTimeout(sgFades, 120); });

  /* ---------------------------------------------------- card rows open on card 1 */
  // Every sideways card row opens on its first card (also after the back button restores the page).
  // Nothing else sets scrollLeft or calls scrollIntoView on load.
  function resetRails() {
    qsa('.os-pdp-lk__cards, .os-pdp-cloth__cards').forEach(function (row) {
      if (row.scrollLeft !== 0 && !row.matches(':hover')) row.scrollLeft = 0;
    });
  }
  window.addEventListener('pageshow', resetRails);


  /* ------------------------------------------------------ curly quotes */
  // Straight quotes and apostrophes in the page's own text (descriptions, fit notes, metafield
  // copy) become curly ones. Text nodes only; code, scripts, inputs and attributes are untouched.
  function curlyText(s) {
    return s
      .replace(/(\w)'(\w)/g, '$1\u2019$2')
      .replace(/(^|[\s(\[\u2014\u2013])'/g, '$1\u2018')
      .replace(/'/g, '\u2019')
      .replace(/(^|[\s(\[\u2014\u2013])"/g, '$1\u201C')
      .replace(/"/g, '\u201D');
  }
  function curlyQuotes() {
    qsa('.os-pdp, .os-pdp-sg').forEach(function (root) {
      var w = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
      var n;
      while ((n = w.nextNode())) {
        var p = n.parentElement;
        if (!p || p.closest('script, style, textarea, code, pre, noscript, template')) continue;
        if (n.__osCurly || (n.nodeValue.indexOf("'") === -1 && n.nodeValue.indexOf('"') === -1)) continue;
        // the model line's height and chest marks are already the proper prime marks
        n.nodeValue = curlyText(n.nodeValue);
        n.__osCurly = true;
      }
    });
  }

  /* -------------------------------------------------------------------- init */

  function init() {
    var viewer = qs('.pswp');
    if (viewer && !viewer.getAttribute('aria-label')) viewer.setAttribute('aria-label', 'Image viewer');
    initDelivery();
    initFilm();
    initCounter();
    initSticky();
    syncSticky();
    initStickyCol();
    syncStock();
    initRecent();
    initMotion();
    initLookRow();
    resetRails();
    initStickyPick();
    initColourwayPreview();
    initReveal();
    curlyQuotes();
  }

  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', init);
  else init();
  window.addEventListener('theme:section:update', init);
  doc.addEventListener('shopify:section:load', init);

  // Test hook (Node and the preview theme): the delivery rules as a pure function.
  window.osPdp = { estimate: estimate };
})();
